import { useState } from "react";
import type * as React from "react";
import { Button, Heading, Icon, Select, Loader } from "@stellar/design-system";
import { isValid, format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { FilterMenu } from "@/components/FilterMenu";
import { Pagination } from "@/components/Pagination";
import { SectionHeader } from "@/components/SectionHeader";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";

import { useAuditLogs } from "@/apiQueries/useAuditLogs";
import { getAuditLogExport } from "@/api/getAuditLogExport";
import { PAGE_LIMIT_OPTIONS } from "@/constants/settings";
import { number } from "@/helpers/formatIntlNumber";
import { localStorageSessionToken } from "@/helpers/localStorageSessionToken";
import { ApiAuditLog, AuditLogSearchParams } from "@/types";

// Convert Date to YYYY-MM-DD for API
const dateToIso = (date: Date | null): string => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

type AuditLogFilters = {
  after_date: Date | null;
  before_date: Date | null;
  action: string;
};

const AUDIT_LOG_ACTIONS = [
  { value: "", label: "All actions" },
  { value: "user_created", label: "User created" },
  { value: "user_deactivated", label: "User deactivated" },
  { value: "user_activated", label: "User activated" },
  { value: "user_role_changed", label: "User role changed" },
  { value: "payment_canceled", label: "Payment canceled" },
  { value: "payment_retried", label: "Payment retried" },
  { value: "direct_payment_sent", label: "Direct payment sent" },
  { value: "disbursement_created", label: "Disbursement created" },
  { value: "disbursement_started", label: "Disbursement started" },
  { value: "disbursement_paused", label: "Disbursement paused" },
  { value: "wallet_recovery_initiated", label: "Wallet recovery initiated" },
  { value: "wallet_unregistered", label: "Wallet unregistered" },
];

export const AuditLogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const initFilters: AuditLogFilters = {
    after_date: null,
    before_date: null,
    action: "",
  };

  const [filters, setFilters] = useState<AuditLogFilters>(initFilters);
  const [queryFilters, setQueryFilters] = useState<AuditLogFilters>(initFilters);

  const afterDateIso = dateToIso(queryFilters.after_date);
  const beforeDateIso = dateToIso(queryFilters.before_date);

  const searchParams: AuditLogSearchParams = {
    page: currentPage.toString(),
    page_limit: pageLimit.toString(),
    ...(afterDateIso ? { after_date: `${afterDateIso}T00:00:00Z` } : {}),
    ...(beforeDateIso ? { before_date: `${beforeDateIso}T23:59:59Z` } : {}),
    ...(queryFilters.action ? { action: queryFilters.action } : {}),
  };

  const {
    data: auditLogs,
    error,
    isLoading,
    isFetching,
  } = useAuditLogs(searchParams);

  const isFiltersSelected = Object.values(filters).some((v) => Boolean(v));
  const isFiltersApplied = Object.values(queryFilters).some((v) => Boolean(v));
  const maxPages = auditLogs?.pagination?.pages || 1;

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setFilters({
      ...filters,
      [event.target.id]: event.target.value,
    });
  };

  const handleFilterSubmit = () => {
    setCurrentPage(1);
    setQueryFilters(filters);
  };

  const handleFilterReset = () => {
    setCurrentPage(1);
    setFilters(initFilters);
    setQueryFilters(initFilters);
  };

  const handlePageLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    setPageLimit(Number(event.target.value));
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const token = localStorageSessionToken.get() || "";
      const exportParams: AuditLogSearchParams = {
        ...(afterDateIso ? { after_date: `${afterDateIso}T00:00:00Z` } : {}),
        ...(beforeDateIso ? { before_date: `${beforeDateIso}T23:59:59Z` } : {}),
        ...(queryFilters.action ? { action: queryFilters.action } : {}),
      };
      await getAuditLogExport(token, exportParams);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const formatActorName = (log: ApiAuditLog) => {
    if (log.first_name || log.last_name) {
      return [log.first_name, log.last_name].filter(Boolean).join(" ");
    }
    if (log.actor_name) return log.actor_name;
    if (log.actor_type === "system") return "System";
    return log.actor_id || "Unknown";
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm">
              {auditLogs?.pagination?.total && auditLogs.pagination.total > 0
                ? `${number.format(auditLogs.pagination.total)} `
                : ""}
              Audit Logs
            </Heading>
          </SectionHeader.Content>
        </SectionHeader.Row>

        <SectionHeader.Row>
          <SectionHeader.Content>
            <FilterMenu
              onSubmit={handleFilterSubmit}
              onReset={handleFilterReset}
              isSubmitDisabled={!isFiltersSelected}
              isResetDisabled={false}
            >
              <div className="FilterMenu__item">
                <Select
                  id="action"
                  fieldSize="sm"
                  label="Action"
                  value={filters.action}
                  onChange={handleFilterChange}
                >
                  {AUDIT_LOG_ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="FilterMenu__item">
                <label className="Label Label--sm" htmlFor="after_date">After date</label>
                <DatePicker
                  id="after_date"
                  selected={filters.after_date}
                  onChange={(date: Date | null) => setFilters({ ...filters, after_date: date })}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  isClearable
                />
              </div>

              <div className="FilterMenu__item">
                <label className="Label Label--sm" htmlFor="before_date">Before date</label>
                <DatePicker
                  id="before_date"
                  selected={filters.before_date}
                  onChange={(date: Date | null) => setFilters({ ...filters, before_date: date })}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  isClearable
                />
              </div>
            </FilterMenu>

            <Button
              variant="tertiary"
              size="md"
              icon={<Icon.Download01 />}
              onClick={handleExport}
              disabled={isLoading || isFetching || isExporting}
            >
              {isExporting ? "Exporting..." : "Export Audit Log"}
            </Button>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <div className="FiltersWithSearch__pageLimit">
              <Select
                id="audit-logs-page-limit"
                fieldSize="md"
                value={pageLimit}
                onChange={handlePageLimitChange}
              >
                {PAGE_LIMIT_OPTIONS.map((p) => (
                  <option key={p} value={p}>{`Show ${p} results`}</option>
                ))}
              </Select>
            </div>

            <Pagination
              currentPage={Number(currentPage)}
              maxPages={Number(maxPages)}
              onSetPage={(page) => setCurrentPage(page)}
              isLoading={isLoading || isFetching}
            />
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      {error && (
        <ErrorWithExtras appError={{ message: error.message }} />
      )}

      {exportError && (
        <ErrorWithExtras appError={{ message: exportError }} />
      )}

      <div className="TableContainer">
        <table className="Table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Admin User</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>
                  <Loader />
                </td>
              </tr>
            )}

            {!isLoading && auditLogs?.data?.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>
                  {isFiltersApplied
                    ? "No audit logs match the selected filters."
                    : "No audit logs recorded yet."}
                </td>
              </tr>
            )}

            {!isLoading &&
              auditLogs?.data?.map((log) => (
                <tr key={log.id}>
                  <td>{formatDate(log.created_at)}</td>
                  <td>
                    <div>{formatActorName(log)}</div>
                    <div>{`(${formatAction(log.actor_type)})`}</div>
                  </td>
                  <td>{formatAction(log.action)}</td>
                  <td>
                    <div>{log.target_name || log.target_id || "—"}</div>
                    {log.target_type && (
                      <div className="Table__note">{formatAction(log.target_type)}</div>
                    )}
                  </td>
                  <td>
                    {log.payload ? (
                      <div className="Table__details">
                        {Object.entries(log.payload).map(([key, value]) => (
                          <div key={key} className="Table__details-item">
                            <span className="Table__details-label">
                              {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:
                            </span>{" "}
                            <span>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
